import React, { useCallback, useMemo, useState } from "react"
import { useParams } from "next/navigation"

import { type RerunMetricsById } from "@/metrics/rerun"
import {
  useDeleteSimulationOutputTransport,
  useRunTransportInvalidation,
} from "@/runs"
import { RUN_STATUS } from "@/runs/status"
import {
  getUserFacingErrorMessage,
  Table,
  TableColumnToggle,
  type ColumnDef,
} from "@coval/ui"
import { addToast, useDisclosure } from "@heroui/react"

import { useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useInvestigatedSimulationOutputs } from "@/hooks/useInvestigatedSimulationOutputs"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { RunRecord } from "@/types/api/RunRecord"
import type { RunRowLike } from "@/types/RunRow"

import { type CompareBy } from "../../compare-by"
import { RunResultsSelectionActionsProvider } from "../contexts/RunResultsSelectionActionsContext"
import { useExplanationSimilarityReorder } from "../hooks/useExplanationSimilarityReorder"
import { useRunResultsBulkDelete } from "../hooks/useRunResultsBulkDelete"
import { useRunResultsColumnDefs } from "../hooks/useRunResultsColumnDefs"
import { useRunResultsFilterActions } from "../hooks/useRunResultsFilterActions"
import {
  useRunResultsFilters,
  type RunResultsFilterController,
} from "../hooks/useRunResultsFilters"
import { useRunResultsMutationCatalog } from "../hooks/useRunResultsMutationCatalog"
import { useRunResultsOutlierRows } from "../hooks/useRunResultsOutlierRows"
import { useRunResultsRenderColumns } from "../hooks/useRunResultsRenderColumns"
import { useRunResultsRowNavigation } from "../hooks/useRunResultsRowNavigation"
import { useRunResultsSelection } from "../hooks/useRunResultsSelection"
import { useRunResultsTableColumns } from "../hooks/useRunResultsTableColumns"
import { useRunResultsTestSetGeneration } from "../hooks/useRunResultsTestSetGeneration"
import type {
  RunResultsMetricFilterAdaptersById,
  RunResultsMetricRenderAdaptersById,
  RunResultsMetricSourcesById,
} from "../types"
import {
  getCompareColor as computeCompareColor,
  getCompareDisplayValue as computeCompareDisplayValue,
  getCompareColumnLabel,
} from "../utils/compareColumn"
import { getMetricDisplayLabel as computeMetricDisplayLabel } from "../utils/metricColumnLabel"
import { RunResultsBulkActions } from "./run-results-table/RunResultsBulkActions"
import { RunResultsCsvExportButton } from "./run-results-table/RunResultsCsvExportButton"
import { RunResultsTableModals } from "./run-results-table/RunResultsTableModals"
import { RunResultsTableSkeleton } from "./run-results-table/RunResultsTableSkeleton"
import { useRunResultRowHrefs } from "./run-results-table/useRunResultRowHrefs"
import { RunFilterDropdown, RunResultsFilterChips } from "./RunFilterDropdown"
import { RunResultsItemCount } from "./RunResultsItemCount"
import { SimilarityThresholdSlider } from "./SimilarityThresholdSlider"

const log = logger("RunResultsTable")

type RunResultsTableRunRecord = Pick<RunRecord, "agent_id"> | null | undefined

interface RunResultsTableProps {
  filters?: RunResultsFilterController
  metricFilterAdaptersById: RunResultsMetricFilterAdaptersById
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  /** Richer per-metric source for type-aware filter value pre-fill (categorical options + metadata). */
  metricSourcesById: RunResultsMetricSourcesById
  runRows: RunRowLike[]
  runRecord: RunResultsTableRunRecord
  onRowClick: (simulationOutputId: string) => void
  /** When set, live rows show a listen affordance that opens the live-sims panel. */
  onListenLive?: (simulationOutputId: string) => void
  onSelectionChange?: (selectedIds: string[]) => void
  isLoading?: boolean
  /** When set, only show this metric's column (e.g. when viewing in-depth run review for that metric). */
  metricIdFilter?: string | null
  /** Multi-run: when set, show compare column; table reads values from RunRow. */
  compareBy?: CompareBy | null
  /** Multi-run: optional display name lookups for compare-by agent/persona. */
  agentsById?: Record<string, { display_name: string }> | null
  personasById?: Record<string, { display_name: string }> | null
  /** When true, disables auth-gated tRPC queries and write actions (for public shared pages). */
  isShared?: boolean
  /** Per-metric visibility config. When provided, hides metric columns where visible === false. */
  metricViews?: Record<string, { visible: boolean }>
  onRowHover?: (simulationOutputId: string | null) => void
  /** Catalog used by the Rerun bulk-action button. When omitted, the bulk-action bar omits rerun. */
  rerunMetricRecordsById?: RerunMetricsById | null
  navigationScopeStorageKey?: string
  /** This run's tag control. It sits with the table's own controls, which is where it reads as belonging. */
  tableTagAction?: React.ReactNode
  /** Actions on this run — resimulate, rerun, review — rendered in the controls row rather than the page header. */
  tableRunActions?: React.ReactNode
  /**
   * Set by callers whose `tableRunActions` read the selection context and host
   * the delete for the selected rows. Arbitrary content carries no such
   * promise, so without this the bulk-action bar keeps its own delete.
   */
  tableActionsHostSelectionDelete?: boolean
  /** The results table's own scroll container. The card around this component clips rather than scrolls, so scroll-position save/restore reads this. */
  scrollRef?: React.Ref<HTMLDivElement | null>
}

export const RunResultsTable: React.FC<RunResultsTableProps> = ({
  filters: controlledFilters,
  metricFilterAdaptersById,
  metricRenderAdaptersById,
  metricSourcesById,
  runRows,
  runRecord,
  onRowClick,
  onListenLive,
  onSelectionChange,
  isLoading = false,
  metricIdFilter = null,
  compareBy = null,
  agentsById = null,
  personasById = null,
  isShared = false,
  metricViews,
  onRowHover,
  rerunMetricRecordsById = null,
  navigationScopeStorageKey,
  tableTagAction,
  tableRunActions,
  tableActionsHostSelectionDelete = false,
  scrollRef,
}) => {
  const internalFilters = useRunResultsFilters({
    metricRecordsById: metricFilterAdaptersById,
    runRows,
  })
  const filters = controlledFilters ?? internalFilters
  const {
    filteredRunRows,
    metricFilters,
    mutationFilterIds,
    isOutliersEnabled,
    outlierMetricIds,
    statusFilterStatuses,
    addMetricFilters,
    clearAllFilters,
    clearMetricFilters,
    setMetricFilters,
    setMutationFilterIds,
    setOutliersEnabled,
    setOutlierMetricIds,
    setStatusFilterStatuses,
  } = filters

  const compareColumnLabel = useMemo(
    () => getCompareColumnLabel(compareBy),
    [compareBy],
  )

  const getCompareDisplayValue = useCallback(
    (row: RunRowLike): string =>
      computeCompareDisplayValue(row, compareBy, agentsById, personasById),
    [compareBy, agentsById, personasById],
  )

  const getCompareColor = useCallback(
    (row: RunRowLike): string | undefined =>
      computeCompareColor(row, compareBy),
    [compareBy],
  )

  const { trackEvent } = useAnalytics()
  const params = useParams()
  const runId =
    typeof params.run_id === "string" && params.run_id.length > 0
      ? params.run_id
      : undefined
  const queryParams = useQueryParams()
  const { invalidateRunDetail, invalidateRunsLists } =
    useRunTransportInvalidation()

  const { markAsInvestigated, isInvestigated } =
    useInvestigatedSimulationOutputs(params.run_id as string | undefined)

  const agentId = runRecord?.agent_id
  const { mutationNameById, mutationById } = useRunResultsMutationCatalog({
    agentId,
    isShared,
    runRows,
  })

  const [isCompactColumns] = useState(true)

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure()

  const [itemsToDelete, setItemsToDelete] = useState<string[]>([])
  const [openSection, setOpenSection] = useState<
    "mutations" | "metrics" | "status" | null
  >(null)
  const handleFilterOpenChange = useCallback((open: boolean) => {
    setOpenSection(open ? "metrics" : null)
  }, [])

  const deleteSimulationOutput = useDeleteSimulationOutputTransport({
    onSuccess: () => {
      addToast({
        title: "Success",
        description: "Run result deleted successfully",
      })
      const invalidations: Promise<unknown>[] = [invalidateRunsLists()]
      if (typeof params.run_id === "string") {
        invalidations.push(invalidateRunDetail(params.run_id))
      }
      void Promise.all(invalidations)
    },
    onError: (error: { message?: string }) => {
      addToast({
        title: "Error",
        description: getUserFacingErrorMessage(error, {
          fallback: "Failed to delete simulation output.",
        }),
        color: "danger",
      })
    },
  })

  const getMetricDisplayLabel = useCallback(
    (metricKey: string, isCompact: boolean) =>
      computeMetricDisplayLabel(metricKey, isCompact, metricRenderAdaptersById),
    [metricRenderAdaptersById],
  )

  // Only show Mutation column when at least one run row has mutation data
  const hasAnyMutations = useMemo(
    () =>
      runRows.some(
        (row) => row.mutation_id != null || row.mutation_name != null,
      ),
    [runRows],
  )
  const showMutationColumn = hasAnyMutations

  const explanationTable = useMemo(
    () =>
      metricIdFilter != null &&
      metricIdFilter !== "" &&
      metricRenderAdaptersById?.[metricIdFilter]
        ? metricRenderAdaptersById[metricIdFilter].getExplanationTable(runRows)
        : { columns: [], getRowValues: () => ({}) as Record<string, string> },
    [metricIdFilter, metricRenderAdaptersById, runRows],
  )

  const explanationSimilarity = useExplanationSimilarityReorder({
    metricIdFilter,
    metricRenderAdaptersById,
    runRows,
  })

  const allColumns = useRunResultsColumnDefs({
    compareColumnLabel,
    explanationTable,
    getMetricDisplayLabel,
    isCompactColumns,
    metricIdFilter,
    metricRenderAdaptersById,
    metricViews,
    runRows,
    showMutationColumn,
  })

  const {
    effectiveVisibleColumns,
    effectiveColumnOrder,
    visibleOrderedColumns,
    setVisibleColumns,
    setColumnOrder,
  } = useRunResultsTableColumns({
    allColumns,
    metricRenderAdaptersById,
  })

  const columnToggleDefs = useMemo<ColumnDef<RunRowLike>[]>(
    () =>
      allColumns.map((column) => ({
        id: column.key,
        header: column.fullLabel || column.label || column.key,
        headerLabel: column.label?.trim() || column.fullLabel || column.key,
      })),
    [allColumns],
  )

  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        allColumns.map((column) => [
          column.key,
          effectiveVisibleColumns.has(column.key),
        ]),
      ),
    [allColumns, effectiveVisibleColumns],
  )

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      setVisibleColumns(
        new Set(Object.keys(visibility).filter((key) => visibility[key])),
      )
    },
    [setVisibleColumns],
  )

  const handleResetColumns = useCallback(() => {
    setVisibleColumns(new Set())
    setColumnOrder([])
  }, [setVisibleColumns, setColumnOrder])

  const {
    getOutlierCellVariant,
    isOutlierAnalysisPending,
    outlierEvidenceBySimulationId,
    outlierSimulationOutputIds,
    outliersEnabled,
    outliersQuery,
    scopedRowDisplayIndex,
    scopedRunRows,
    selectedOutliers,
  } = useRunResultsOutlierRows({
    filteredRunRows,
    isOutliersEnabled,
    isShared,
    metricIdFilter,
    metricRenderAdaptersById,
    outlierMetricIds,
    reorderRows: explanationSimilarity.reorder,
    runId,
  })

  const {
    setSelectedKeys,
    selectableSimulationOutputIds,
    selectedSimulationOutputIds,
    selectedCount,
  } = useRunResultsSelection({
    scopedRunRows,
    onSelectionChange,
  })

  const cancelledSelectionDisabledReason = useMemo(() => {
    if (selectedSimulationOutputIds.length === 0) {
      return undefined
    }
    const selectedIdSet = new Set(selectedSimulationOutputIds)
    const hasCancelledSelection = scopedRunRows.some(
      (row) =>
        selectedIdSet.has(row.simulation_output_id) &&
        row.error_status === RUN_STATUS.CANCELLED,
    )
    return hasCancelledSelection
      ? "One or more selected simulations were cancelled."
      : undefined
  }, [scopedRunRows, selectedSimulationOutputIds])

  const rerunMetricsCount =
    metricIdFilter != null && metricIdFilter !== ""
      ? 1
      : Object.keys(rerunMetricRecordsById ?? {}).length
  const rerunMetricsLabel = `Rerun ${rerunMetricsCount} Metric${
    rerunMetricsCount === 1 ? "" : "s"
  }`

  const { getResultHref, rowCopyId, rowHref } = useRunResultRowHrefs({
    queryParams,
    runId: String(params.run_id),
    slug: params.slug as string,
  })

  const {
    handlePointerEnter,
    handlePointerLeave,
    handleRowClick,
    visibleSimulationOutputId,
  } = useRunResultsRowNavigation({
    getResultHref,
    markAsInvestigated,
    navigationScopeStorageKey,
    onRowClick,
    onRowHover,
    runId: typeof params.run_id === "string" ? params.run_id : undefined,
    scopedRunRows,
    trackEvent,
  })

  const tableColumns = useRunResultsRenderColumns({
    agentsById,
    compareColumnLabel,
    explanationTable,
    getCompareColor,
    getCompareDisplayValue,
    metricIdFilter,
    metricRenderAdaptersById,
    mutationById,
    mutationNameById,
    onListenLive,
    outlierEvidenceBySimulationId,
    runRecord,
    scopedRowDisplayIndex,
    scopedRunRows,
    visibleOrderedColumns,
  })

  const getTestSetHref = useCallback(
    (testSetId: string) =>
      queryParams.getHref(`/test-sets/${testSetId}`, params.slug as string),
    [params.slug, queryParams],
  )
  const {
    close: closeGenerateTestSetModal,
    confirm: confirmGenerateTestSet,
    isOpen: isGenerateTestSetModalOpen,
    isPending: isGeneratingTestSet,
    open: openGenerateTestSetModal,
  } = useRunResultsTestSetGeneration({
    getTestSetHref,
    scopedRunRows,
    selectedSimulationOutputIds,
  })

  const {
    handleAddMetricFilter,
    handleClearAllFilters,
    handleClearAllMetricFilters,
    handleRemoveMetricFilter,
    handleRemoveMutationFilter,
    handleRemoveStatusFilter,
  } = useRunResultsFilterActions({
    addMetricFilters,
    clearAllFilters,
    clearMetricFilters,
    metricFilters,
    mutationFilterIds,
    setMetricFilters,
    setMutationFilterIds,
    setStatusFilterStatuses,
    statusFilterStatuses,
  })

  const { confirmBulkDelete, handleBulkDelete } = useRunResultsBulkDelete({
    deleteSimulationOutput,
    itemsToDelete,
    log,
    onCloseDeleteModal,
    onOpenDeleteModal,
    selectedSimulationOutputIds,
    setItemsToDelete,
    setSelectedKeys,
  })

  const selectionActionsContextValue = useMemo(
    () => ({
      isDeletingSelected: deleteSimulationOutput.isPending,
      onDeleteSelected: handleBulkDelete,
      selectedCount,
    }),
    [deleteSimulationOutput.isPending, handleBulkDelete, selectedCount],
  )

  // While the rows load, the table's own controls have nothing to act on, so the
  // row is worth keeping only for the run-level actions -- a run still fetching
  // its rows is one the user may need to cancel, or tag. Without them there is
  // nothing to put in it, and the skeleton stands alone as before.
  const showControlsRow = !isLoading || !!tableRunActions || !!tableTagAction

  return (
    <div className="w-full h-full flex flex-col">
      {showControlsRow && (
        <div className="flex-none border-b border-coval-border bg-background px-6 py-3">
          <div className="flex items-start gap-2 justify-between">
            {/* Left aligned section: the table's own controls, or the actions for
              whatever rows are selected. The run-level actions after it stay put
              either way -- they act on the run, not on the selection.
              The row wraps, so where it is too narrow to hold everything the
              run's actions drop to a second line under the table's controls
              instead of the two clusters colliding. */}
            <div className="flex flex-wrap items-center gap-2 flex-1">
              {isLoading ? null : selectedCount === 0 ? (
                <>
                  {/* Column selection */}
                  <TableColumnToggle
                    columns={columnToggleDefs}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                    columnOrder={effectiveColumnOrder}
                    onColumnOrderChange={setColumnOrder}
                    onReset={handleResetColumns}
                  />

                  <RunResultsCsvExportButton
                    allColumns={allColumns}
                    columnOrder={effectiveColumnOrder}
                    compareColumnLabel={compareColumnLabel}
                    explanationTable={explanationTable}
                    getCompareDisplayValue={getCompareDisplayValue}
                    metricRenderAdaptersById={metricRenderAdaptersById}
                    mutationNameById={mutationNameById}
                    rows={scopedRunRows}
                    visibleColumns={effectiveVisibleColumns}
                  />

                  <RunFilterDropdown
                    isOpen={openSection !== null}
                    onOpenChange={handleFilterOpenChange}
                    mutationNameById={mutationNameById}
                    mutationFilterIds={mutationFilterIds}
                    onMutationFilterChange={setMutationFilterIds}
                    statusFilterStatuses={statusFilterStatuses}
                    onStatusFilterChange={setStatusFilterStatuses}
                    isOutliersEnabled={outliersEnabled}
                    onOutliersEnabledChange={
                      !isShared && runId ? setOutliersEnabled : undefined
                    }
                    outlierMetricIds={outlierMetricIds}
                    outlierMetricOptions={
                      selectedOutliers.availableMetricOptions
                    }
                    onOutlierMetricIdsChange={
                      !isShared && runId ? setOutlierMetricIds : undefined
                    }
                    metricRecordsById={metricRenderAdaptersById}
                    metricSourcesById={metricSourcesById}
                    runRows={runRows}
                    metricFilters={metricFilters}
                    onAddMetricFilter={handleAddMetricFilter}
                    onRemoveMetricFilter={handleRemoveMetricFilter}
                    onClearAllMetricFilters={handleClearAllMetricFilters}
                  />

                  <RunResultsFilterChips
                    mutationFilterIds={mutationFilterIds}
                    mutationNameById={mutationNameById}
                    onRemoveMutation={handleRemoveMutationFilter}
                    statusFilterStatuses={statusFilterStatuses}
                    onRemoveStatus={handleRemoveStatusFilter}
                    metricFilters={metricFilters}
                    metricRecordsById={metricRenderAdaptersById}
                    onRemoveMetricFilter={handleRemoveMetricFilter}
                    onClearAllFilters={handleClearAllFilters}
                  />
                </>
              ) : (
                <RunResultsBulkActions
                  cancelledSelectionDisabledReason={
                    cancelledSelectionDisabledReason
                  }
                  isGeneratingTestSet={isGeneratingTestSet}
                  isShared={isShared}
                  metricIdFilter={metricIdFilter}
                  onBulkDelete={handleBulkDelete}
                  onGenerateTestSet={openGenerateTestSetModal}
                  rerunMetricRecordsById={rerunMetricRecordsById}
                  rerunMetricsCount={rerunMetricsCount}
                  rerunMetricsLabel={rerunMetricsLabel}
                  selectableSimulationOutputIds={selectableSimulationOutputIds}
                  selectedSimulationOutputIds={selectedSimulationOutputIds}
                  showDeleteAction={!tableActionsHostSelectionDelete}
                />
              )}
              {/* Tagging acts on the run, not on the rows, so it stays put
                  while they load rather than appearing once they arrive. */}
              {tableTagAction}
              {/* The row count reads as a status, not a control, so it sits
                  apart from them at the right edge of the first line -- ahead
                  of the run's actions, so it stays there when they wrap. */}
              {isLoading || selectedCount > 0 ? null : (
                <div className="ml-auto">
                  <RunResultsItemCount
                    hasExplicitNoSelection={
                      selectedOutliers.hasExplicitNoSelection
                    }
                    hasLowSampleWarning={selectedOutliers.hasLowSampleWarning}
                    hasNoUsableMetricData={
                      selectedOutliers.hasNoUsableMetricData
                    }
                    hasOutlierError={outliersQuery.isError}
                    isOutlierAnalysisPending={isOutlierAnalysisPending}
                    itemCount={scopedRunRows.length}
                    outlierCount={outlierSimulationOutputIds.size}
                    outliersEnabled={outliersEnabled}
                  />
                </div>
              )}
              {/* The run-level actions render here rather than beside the
                  table, so the selection context reaches them and their
                  overflow menu can host the delete for whatever is selected. */}
              <RunResultsSelectionActionsProvider
                value={selectionActionsContextValue}
              >
                {tableRunActions}
              </RunResultsSelectionActionsProvider>
            </div>

            {!isLoading &&
            selectedCount === 0 &&
            explanationSimilarity.isActive ? (
              <SimilarityThresholdSlider
                value={explanationSimilarity.similarityThreshold}
                onChange={explanationSimilarity.setSimilarityThreshold}
                className="px-2"
              />
            ) : null}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex-1 min-h-0 min-w-0 bg-background">
          <RunResultsTableSkeleton rowCount={5} columnCount={6} />
        </div>
      ) : (
        <div
          data-testid="run-results-table"
          className="flex-1 min-h-0 min-w-0 bg-background"
          data-covi-view-list="simulation"
          data-covi-list-total={scopedRunRows.length}
        >
          <Table
            scrollRef={scrollRef}
            aria-label="Run results table"
            data={scopedRunRows}
            cellVariant={getOutlierCellVariant}
            columns={tableColumns}
            getRowId={(row) =>
              row.simulation_output_id ||
              `sim-${scopedRowDisplayIndex.get(row) ?? 0}`
            }
            rowCopyId={rowCopyId}
            rowHref={rowHref}
            onRowClick={(row, event) =>
              handleRowClick(row.simulation_output_id ?? "", event)
            }
            activeRowId={visibleSimulationOutputId}
            onRowPointerEnter={handlePointerEnter}
            onRowPointerLeave={handlePointerLeave}
            rowAttributes={(row) =>
              row.simulation_output_id
                ? {
                    "data-testid": `run-result-row-${row.simulation_output_id}`,
                    "data-simulation-output-id": row.simulation_output_id,
                    // Highlight/view-context row anchor: which of this run's simulations
                    // are on screen (with status) for Sofia's view context.
                    "data-covi-highlight-id": row.simulation_output_id,
                    "data-covi-status": row.error_status ?? undefined,
                  }
                : {}
            }
            isRowUnread={(row) =>
              row.simulation_output_id
                ? !isInvestigated(row.simulation_output_id)
                : false
            }
            emptyState="No results found"
            className="h-full"
            selectable
            selectedIds={new Set(selectedSimulationOutputIds)}
            onSelectionChange={(ids: Set<string>) =>
              setSelectedKeys(new Set(ids))
            }
            isRowSelectable={(row: RunRowLike) =>
              typeof row.simulation_output_id === "string" &&
              row.simulation_output_id.length > 0
            }
          />
        </div>
      )}

      <RunResultsTableModals
        deleteCount={itemsToDelete.length}
        isDeleteModalOpen={isDeleteModalOpen}
        isDeletePending={deleteSimulationOutput.isPending}
        isGenerateTestSetModalOpen={isGenerateTestSetModalOpen}
        isGeneratingTestSet={isGeneratingTestSet}
        onCloseDeleteModal={onCloseDeleteModal}
        onCloseGenerateTestSet={closeGenerateTestSetModal}
        onConfirmBulkDelete={() => {
          void confirmBulkDelete()
        }}
        onConfirmGenerateTestSet={() => {
          void confirmGenerateTestSet()
        }}
        selectedCount={selectedCount}
      />
    </div>
  )
}
